import React, { useState } from 'react';
import PropTypes from "prop-types";
import { Table, Button, UncontrolledTooltip, Badge } from 'reactstrap';
import { translate } from "react-translate";
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';
import { actionSetEditData } from 'store/actions/usersActions';
import { useDispatch } from "react-redux";
import { useHistory } from 'react-router-dom';
import DoctorDetailsModal from './DoctorDetailsModal';

/**
 * @component TableDoctorUsers
 * @description Renders the doctor management table. Uses `total_practices` from
 * the list payload (numeric field) instead of the removed `associated_details`
 * array. Detail hydration is deferred to the modal via a server-side call.
 *
 * @param {Object}  props
 * @param {Array}   props.data    - Array of doctor objects from the paginated list.
 * @param {boolean} props.loading - When true renders a local loading spinner.
 */
const TableDoctorUsers = (props) => {
  const { data, loading } = props;
  const { t } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  /**
   * Navigates to the doctor edit form and seeds Redux with the selected row.
   *
   * @param {React.SyntheticEvent} e
   * @param {Object} user - Doctor row object.
   */
  const editUser = (e, user) => {
    e.preventDefault();
    dispatch(actionSetEditData({ ...user }));
    history.push(`/admin/list-users/edit/`);
  };

  /**
   * Opens the details modal for the selected doctor.
   * The modal is responsible for fetching `associated_details` server-side.
   *
   * @param {React.SyntheticEvent} e
   * @param {Object} user - Doctor row object.
   */
  const viewDetails = (e, user) => {
    e.preventDefault();
    setSelectedDoctor(user);
    setModalOpen(true);
  };

  /** @returns {void} */
  const toggleModal = () => setModalOpen((prev) => !prev);

  return (
    <>
      <DoctorDetailsModal
        isOpen={modalOpen}
        toggle={toggleModal}
        doctor={selectedDoctor}
      />

      {loading ? (
        <LoadingLocal />
      ) : (
        <div className="table-data">
          <Table responsive>
            <thead className="text-primary">
              <tr>
                <th>{t("firstName-user")} / {t("lastName-user")}</th>
                <th>{t("email-user")}</th>
                <th>{t("location-user")}</th>
                <th>{t("type-user")}</th>
                <th className="text-center">{t("relations-user")}</th>
                <th className="text-right">{t("actions")}</th>
              </tr>
            </thead>

            {data.length > 0 &&
              data.map((p, index) => (
                <tbody key={index}>
                  <tr>
                    <td>
                      <span className="font-weight-bold">{p.lastName}</span>, {p.firstName}
                      <br />
                      <small className="text-muted">{p.title}</small>
                    </td>
                    <td>
                      {p.email}
                      <br />
                      <small className="text-muted">{p.phone}</small>
                    </td>
                    <td>{p.location || '-'}</td>
                    <td>
                      <Badge color={p.membership === 'full' ? 'success' : 'secondary'}>
                        {p.membership}
                      </Badge>
                    </td>

                    <td className="text-center">
                      <div className="summary-badge">
                        <i className="fa fa-hospital-o mr-1" />
                        <span className="summary-highlight">
                          {typeof p.total_practices === 'number' ? p.total_practices : '-'}
                        </span>{' '}
                        {t("practice-count-label")}
                      </div>
                    </td>

                    <td className="text-right">
                      <Button
                        onClick={(e) => viewDetails(e, p)}
                        className="btn-icon btn-round btn-neutral"
                        color="info"
                        size="sm"
                        id={`tooltip-details-${index}`}
                      >
                        <i className="fa fa-eye" />
                      </Button>
                      <UncontrolledTooltip placement="top" target={`tooltip-details-${index}`}>
                        {t("view-details")}
                      </UncontrolledTooltip>

                      <Button
                        onClick={(e) => editUser(e, p)}
                        className="btn-icon btn-round btn-neutral"
                        color="warning"
                        size="sm"
                        id={`tooltip-edit-${index}`}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <UncontrolledTooltip placement="top" target={`tooltip-edit-${index}`}>
                        {t("edit-action")}
                      </UncontrolledTooltip>
                    </td>
                  </tr>
                </tbody>
              ))}
          </Table>
        </div>
      )}
    </>
  );
};

TableDoctorUsers.defaultProps = {
  data: [],
  loading: true,
};

TableDoctorUsers.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
};

export default translate("userList-table")(TableDoctorUsers);